/**
 * Per-tenant theming.
 *
 * The design system stores every colour as an oklch triple in a CSS custom
 * property (see `src/index.css`). Applying a tenant brand colour therefore means
 * converting the tenant's hex to oklch and overriding a handful of properties on
 * `documentElement` at runtime — no rebuild, no per-tenant CSS bundle.
 *
 * oklch is used rather than hex because hover/active/subtle variants can be
 * derived by moving lightness while keeping hue and chroma, which keeps a tint
 * recognisably the same colour.
 */

export interface Oklch {
    /** Perceptual lightness, 0–1. */
    l: number;
    /** Chroma, 0–~0.4. */
    c: number;
    /** Hue angle in degrees, 0–360. */
    h: number;
}

/** Tenant-configurable colours. All optional: unset fields keep the defaults. */
export interface TenantTheme {
    /** Brand colour used for primary buttons, links, active nav and charts. */
    primary?: string | null;
    /** Secondary brand colour used for the second chart series and accents. */
    accent?: string | null;
    /** Corner radius in px for the whole UI. */
    radius?: number | null;
    /** Colour mode the tenant's users get before they pick one themselves. */
    defaultMode?: 'light' | 'dark' | 'system' | null;
}

/* ------------------------------------------------------------------ *
 * Colour conversion
 * ------------------------------------------------------------------ */

function srgbToLinear(channel: number): number {
    return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}

function linearToSrgb(channel: number): number {
    return channel <= 0.0031308 ? channel * 12.92 : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
}

/** Parses `#rgb`, `#rrggbb` (with or without `#`) into 0–255 channels. */
export function parseHex(hex?: string | null): { r: number; g: number; b: number } | null {
    if (!hex || typeof hex !== 'string') return null;

    let value = hex.trim().replace(/^#/, '');
    if (value.length === 3) {
        value = value
            .split('')
            .map(ch => ch + ch)
            .join('');
    }
    if (value.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(value)) return null;

    return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16),
    };
}

/** sRGB hex to OKLCH. Returns null when the hex cannot be parsed. */
export function hexToOklch(hex?: string | null): Oklch | null {
    const rgb = parseHex(hex);
    if (!rgb) return null;

    const r = srgbToLinear(rgb.r / 255);
    const g = srgbToLinear(rgb.g / 255);
    const b = srgbToLinear(rgb.b / 255);

    // Linear sRGB -> LMS (Ottosson's OKLab matrices)
    const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
    const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
    const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

    const l_ = Math.cbrt(l);
    const m_ = Math.cbrt(m);
    const s_ = Math.cbrt(s);

    const okL = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
    const okA = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
    const okB = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

    const chroma = Math.sqrt(okA * okA + okB * okB);
    let hue = (Math.atan2(okB, okA) * 180) / Math.PI;
    if (hue < 0) hue += 360;

    return { l: okL, c: chroma, h: hue };
}

/**
 * OKLCH back to an `#rrggbb` string.
 *
 * Out-of-gamut colours are clamped per channel, which shifts hue slightly; this
 * is only used for previews and round-trips, never on the runtime path, where
 * conversion always runs hex -> oklch from an in-gamut colour input.
 */
export function oklchToHex({ l, c, h }: Oklch): string {
    const hRad = (h * Math.PI) / 180;
    const a = c * Math.cos(hRad);
    const b = c * Math.sin(hRad);

    const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = l - 0.0894841775 * a - 1.291485548 * b;

    const lCubed = l_ * l_ * l_;
    const mCubed = m_ * m_ * m_;
    const sCubed = s_ * s_ * s_;

    const r = 4.0767416621 * lCubed - 3.3077115913 * mCubed + 0.2309699292 * sCubed;
    const g = -1.2684380046 * lCubed + 2.6097574011 * mCubed - 0.3413193965 * sCubed;
    const bl = -0.0041960863 * lCubed - 0.7034186147 * mCubed + 1.707614701 * sCubed;

    const toByte = (channel: number) =>
        Math.round(Math.min(1, Math.max(0, linearToSrgb(channel))) * 255)
            .toString(16)
            .padStart(2, '0');

    return `#${toByte(r)}${toByte(g)}${toByte(bl)}`;
}

/** Serialises for CSS, e.g. `oklch(0.478 0.228 252.97)`. */
export function formatOklch({ l, c, h }: Oklch): string {
    return `oklch(${l.toFixed(4)} ${c.toFixed(4)} ${h.toFixed(2)})`;
}

/* ------------------------------------------------------------------ *
 * Contrast
 * ------------------------------------------------------------------ */

/** WCAG relative luminance of an sRGB hex, 0–1. */
export function relativeLuminance(hex: string): number {
    const rgb = parseHex(hex);
    if (!rgb) return 0;
    const r = srgbToLinear(rgb.r / 255);
    const g = srgbToLinear(rgb.g / 255);
    const b = srgbToLinear(rgb.b / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours, 1–21. */
export function contrastRatio(a: string, b: string): number {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const lighter = Math.max(la, lb);
    const darker = Math.min(la, lb);
    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Picks the readable foreground for a background, as an oklch near-black or
 * near-white matching the tokens already in index.css.
 */
export function readableForeground(backgroundHex: string): Oklch {
    const white = { l: 0.985, c: 0, h: 0 };
    const black = { l: 0.145, c: 0.024, h: 225 };
    return contrastRatio(backgroundHex, '#ffffff') >= contrastRatio(backgroundHex, '#111827')
        ? white
        : black;
}

/** True when white-on-colour or black-on-colour clears the WCAG AA 4.5:1 bar. */
export function hasAccessibleContrast(hex: string): boolean {
    return Math.max(contrastRatio(hex, '#ffffff'), contrastRatio(hex, '#111827')) >= 4.5;
}

/* ------------------------------------------------------------------ *
 * Applying a theme
 * ------------------------------------------------------------------ */

/** Shifts lightness while keeping hue and chroma, clamped to a usable range. */
function withLightness(base: Oklch, delta: number): Oklch {
    return { ...base, l: Math.min(0.97, Math.max(0.12, base.l + delta)) };
}

/**
 * Writes the tenant theme onto an element (default `documentElement`) as inline
 * custom properties, which win over the stylesheet's `:root` and `.dark` rules.
 *
 * Only the brand-derived properties are overridden. Neutrals, surfaces and the
 * status colours stay as designed, so contrast and the light/dark split keep
 * working whatever brand colour a tenant picks.
 */
export function applyTenantTheme(theme: TenantTheme, target?: HTMLElement): void {
    const el = target ?? document.documentElement;

    const primary = hexToOklch(theme.primary);
    if (primary) {
        const primaryCss = formatOklch(primary);
        el.style.setProperty('--primary', primaryCss);
        el.style.setProperty(
            '--primary-foreground',
            formatOklch(readableForeground(theme.primary!))
        );
        el.style.setProperty('--ring', primaryCss);
        el.style.setProperty('--sidebar-primary', primaryCss);
        el.style.setProperty('--sidebar-ring', primaryCss);
        // Chart ramp: keep hue, walk lightness so series stay distinguishable.
        el.style.setProperty('--chart-1', primaryCss);
        el.style.setProperty('--chart-3', formatOklch(withLightness(primary, 0.09)));
        el.style.setProperty('--chart-5', formatOklch(withLightness(primary, 0.18)));
    }

    const accent = hexToOklch(theme.accent);
    if (accent) {
        el.style.setProperty('--chart-2', formatOklch(accent));
        el.style.setProperty('--chart-4', formatOklch(withLightness(accent, 0.1)));
    }

    if (typeof theme.radius === 'number' && Number.isFinite(theme.radius)) {
        el.style.setProperty('--radius', `${Math.min(24, Math.max(0, theme.radius))}px`);
    }
}

/** Removes every property `applyTenantTheme` may have set. */
export function clearTenantTheme(target?: HTMLElement): void {
    const el = target ?? document.documentElement;
    [
        '--primary',
        '--primary-foreground',
        '--ring',
        '--sidebar-primary',
        '--sidebar-ring',
        '--chart-1',
        '--chart-2',
        '--chart-3',
        '--chart-4',
        '--chart-5',
        '--radius',
    ].forEach(prop => el.style.removeProperty(prop));
}

/* ------------------------------------------------------------------ *
 * Presets
 * ------------------------------------------------------------------ */

export interface ThemePreset {
    id: string;
    name: string;
    primary: string;
    accent: string;
}

/** Starting points offered in the branding settings; all pass WCAG AA. */
export const THEME_PRESETS: ThemePreset[] = [
    // Closest in-gamut hex to the --primary token shipped in index.css, which is
    // itself marginally outside sRGB and so has no exact hex form.
    { id: 'default', name: 'Quantix Blue', primary: '#2563eb', accent: '#0ea5e9' },
    { id: 'indigo', name: 'Indigo', primary: '#4f46e5', accent: '#a855f7' },
    { id: 'emerald', name: 'Emerald', primary: '#059669', accent: '#14b8a6' },
    { id: 'amber', name: 'Amber', primary: '#b45309', accent: '#f59e0b' },
    { id: 'rose', name: 'Rose', primary: '#e11d48', accent: '#f97316' },
    { id: 'slate', name: 'Graphite', primary: '#334155', accent: '#64748b' },
];
